import axios from "axios";
import constants from "../../../Util/constants";
import { firstGoodAnswer } from "../../../Util/firstGoodAnswer";

// A villa's calendar, from its hub (GET <hub>/local/load-fullcalendar/<id>).
//
// The villa page prices the stay from it, so a booking waits for it (Util/bookablePrice.js). It
// used to be asked for once, with no time limit, and only after the villa page's own code had
// loaded: several seconds after the click on a slow connection, and for ever if that one request
// hung. Now:
//   - the card that is clicked asks straight away (prefetchFullCalendar), and the villa page that
//     opens a moment later picks the same answer up;
//   - a request that stays silent gets a second one beside it, and a third; the first answer wins;
//   - every request has a time limit;
//   - the caller is told whether a hub answered at all, so it can ask again by itself.

export const CALENDAR_TIMEOUT_MS = 8000;
export const CALENDAR_HEDGE_MS = 1500;
export const CALENDAR_TRIES = 3;
// when no hub could be reached, the villa page asks again by itself: this often, this far apart
export const CALENDAR_ASK_AGAIN_TIMES = 5;
export const CALENDAR_ASK_AGAIN_MS = 3000;
// one answer serves the card, the villa page and the booking page that follows it
export const CALENDAR_REUSE_MS = 60 * 1000;

const hasDays = (data) => Boolean(data && data.status && Array.isArray(data.fullCalendar) && data.fullCalendar.length > 0);

// asking again cannot change a refusal, except a sign-in that is being renewed or a busy server
const refusedForGood = (error) => {
  const status = error && error.response && error.response.status;
  return status >= 400 && status < 500 && ![401, 408, 429].includes(status);
};

const askHub = (reqInstance, base, id) => () =>
  reqInstance.get(`${base}/local/load-fullcalendar/${id}`, { timeout: CALENDAR_TIMEOUT_MS }).then(
    (response) => (hasDays(response.data) ? { good: true, value: response.data.fullCalendar } : { final: true }),
    (error) => {
      if (refusedForGood(error)) return { final: true };
      throw error;
    }
  );

const fetchCalendar = async (id, source) => {
  const reqInstance = axios.create({
    headers: {
      Authorization: `Bearer ${constants.SHUB_STATIC_TOKEN}`,
    },
  });
  // Ask the listing's own hub first (property._source is stamped by the merged search:
  // 'SHUB' → api.triangle.luxury, else VTHUB), then the other: ids are hub-unique, so at most
  // one hub answers with days.
  const bases = source === "SHUB"
    ? [constants.SHUB_SOURCE_URL, constants.SHUB_URL]
    : [constants.SHUB_URL, constants.SHUB_SOURCE_URL];
  let everyHubAnswered = true;
  for (const base of bases) {
    const result = await firstGoodAnswer(askHub(reqInstance, base, id), { tries: CALENDAR_TRIES, hedgeAfterMs: CALENDAR_HEDGE_MS });
    if (result.answered && result.value) return { calendar: result.value, answered: true };
    if (!result.answered) {
      everyHubAnswered = false;
      console.error(`Error loading fullcalendar from ${base}: no answer in ${result.tries} tries`);
    }
  }
  return { calendar: [], answered: everyHubAnswered };
};

const kept = new Map(); // villa id -> { at, promise }

const shared = (id, source) => {
  const now = Date.now();
  for (const [key, entry] of kept) if (now - entry.at >= CALENDAR_REUSE_MS) kept.delete(key);
  const have = kept.get(id);
  if (have) return have.promise;
  const promise = fetchCalendar(id, source).then(
    (result) => {
      // only an answer with days is worth keeping: anything else is asked for again next time
      if (!result.calendar.length && kept.get(id) && kept.get(id).promise === promise) kept.delete(id);
      return result;
    },
    () => {
      if (kept.get(id) && kept.get(id).promise === promise) kept.delete(id);
      return { calendar: [], answered: false };
    }
  );
  kept.set(id, { at: now, promise });
  return promise;
};

// { calendar, answered }. `calendar` is [] when there are no days; `answered` is false when no
// hub could be reached, true when the hubs answered and simply have no days for this villa.
// Every caller gets its own copy of the days.
export const loadfullcalendarAnswer = async (id, source) => {
  if (!id) return { calendar: [], answered: true };
  const result = await shared(id, source);
  return { calendar: result.calendar.map((day) => (day && typeof day === "object" ? { ...day } : day)), answered: result.answered };
};

// the days alone, [] when there are none: what callers always got
export const loadfullcalendar = async (id, source) => (await loadfullcalendarAnswer(id, source)).calendar;

// start asking now, for a page that is about to open
export const prefetchFullCalendar = (id, source) => {
  if (id) shared(id, source);
};

// for tests
export const forgetCalendars = () => kept.clear();
